import { Box, Stack, Typography } from '@mui/material';

import { LinkButton } from '@/components/form/baseInputs/Link';
import { MultiSelectInput } from '@/components/form/baseInputs/MultiSelectInput';
import { Loader } from '@/components/progress/Loader/Loader';

import { usePresentationTemplateSelection } from './hooks/usePresentationTemplateSelection';
import { TemplateCard } from './PresentationTemplateSelectionStep.TemplateCard';
import { OnboardingPresentationTemplate } from './PresentationTemplateSelectionStep.utils';

interface PresentationContentProps {
  pinnedTemplates: OnboardingPresentationTemplate[];
  availableTemplates: OnboardingPresentationTemplate[];
  loading?: boolean;
}

export function PresentationContent({
  pinnedTemplates,
  availableTemplates,
  loading = false,
}: PresentationContentProps) {
  const {
    completeOnboarding,
    displayedTemplates,
    isCompleting,
    options,
    selectOtherTemplates,
    selectedOtherTemplateIds,
    selectedTemplateIds,
    toggleTemplate,
  } = usePresentationTemplateSelection({
    availableTemplates,
    loading,
    pinnedTemplates,
  });

  return (
    <Stack
      spacing={2}
      alignItems="center"
      maxWidth={1100}
      height="100%"
      minHeight={0}
      mx="auto"
    >
      <Stack spacing={1} alignItems="center" maxWidth={980}>
        <Typography variant="title-l">Create client presentation</Typography>
        <Typography variant="body-m" textAlign="center">
          Luminary creates client-ready presentations as soon as client profile
          creation is complete. Select the templates you would like to start
          with - you can always add or remove slides once each presentation is
          created.
        </Typography>
      </Stack>

      {loading ? (
        <Loader boxProps={{ py: 8 }} />
      ) : (
        <>
          {displayedTemplates.length > 0 ? (
            <Box
              data-testid="template-card-container"
              sx={{
                width: '100%',
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                pr: 0.5,
              }}
            >
              <Box
                data-testid="template-card-grid"
                sx={{
                  display: 'grid',
                  gridTemplateColumns:
                    displayedTemplates.length === 1
                      ? 'minmax(0, 540px)'
                      : 'repeat(2, minmax(0, 1fr))',
                  gridAutoRows: '1fr',
                  justifyContent: 'center',
                  alignItems: 'stretch',
                  gap: 2,
                }}
              >
                {displayedTemplates.map((template) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    isSelected={selectedTemplateIds.includes(template.id)}
                    onToggle={() => toggleTemplate(template.id)}
                  />
                ))}
              </Box>
            </Box>
          ) : (
            <Typography variant="body-s">
              No presentation templates are available.
            </Typography>
          )}
        </>
      )}
      <Stack
        direction="row"
        alignItems="flex-end"
        justifyContent="space-between"
        width="100%"
        gap={2}
      >
        <Box width={360}>
          <MultiSelectInput
            id="other-presentation-templates"
            label="Other templates"
            options={options}
            value={selectedOtherTemplateIds}
            disabled={loading}
            onChange={(event) => {
              const value = event.target.value;
              let otherTemplateIds: string[];
              if (typeof value === 'string') {
                otherTemplateIds = value.split(',');
              } else if (Array.isArray(value)) {
                otherTemplateIds = value;
              } else {
                return;
              }
              selectOtherTemplates(otherTemplateIds);
            }}
            renderValue={(selected) => {
              const templateIds = selected as string[];
              return templateIds.length === 0
                ? 'Select templates'
                : `${templateIds.length} template${templateIds.length === 1 ? '' : 's'} selected`;
            }}
          />
        </Box>
        <LinkButton
          display="I do not want to create a presentation"
          onClick={() => completeOnboarding([])}
          disabled={isCompleting}
          sx={{ mb: 1.25 }}
        />
      </Stack>
    </Stack>
  );
}
